import { AVAILABLE_SLUG, AVAILABLE_SOON_SLUG } from "@context/search/queries"
import {
  UNITS_EXTENDABLE_AREA,
  LARGE_SITE_AREA,
  LARGE_AREA_FILTERS,
} from "@helpers/constants"
import { findDistricts } from "@helpers/templates"

export const SEARCH_TERM_CHANGE = Symbol("SEARCH_TERM_CHANGE")
export const PRICE_RANGE_CHANGE = Symbol("PRICE_RANGE_CHANGE")
export const CHECKBOX_GROUP_CHANGE = Symbol("CHECKBOX_GROUP_CHANGE")
export const FILTER_SITES = Symbol("FILTER_SITES")
export const CLEAR_FILTERS = Symbol("CLEAR_FILTERS")

export const filterInvestorSites = (
  data,
  { siteTypes, siteFundTypes, siteStatuses, siteArea, siteSustainability },
  callback
) => {
  const filteredData = data
    //Site type filter
    .filter(({ taxonomySiteTypes: { nodes: taxonomySiteTypes } }) => {
      if (!Object.keys(siteTypes).length) return true

      for (const key in siteTypes)
        if (taxonomySiteTypes[0].slug === key) return true

      return false
    })
    // Site fund type filter
    .filter(({ taxonomyFundTypes: { nodes: taxonomyFundTypes } }) => {
      if (!Object.keys(siteFundTypes).length) return true

      for (const key in siteFundTypes)
        if (taxonomyFundTypes[0].slug === key) return true

      return false
    })
    // Site statuses filter
    .filter(({ taxonomySiteStatuses: { nodes: taxonomySiteStatuses } }) => {
      if (!Object.keys(siteStatuses).length) return true

      for (const key in siteStatuses)
        if (taxonomySiteStatuses[0].slug === key) return true

      return false
    })
    // region filter
    .filter(({ taxonomySiteAreas: { nodes: taxonomySiteAreas } }) => {
      if (!Object.keys(siteArea).length) return true

      for (const key in siteArea) {
        if (taxonomySiteAreas.some(({ slug }) => slug === key)) {
          return true
        }
      }

      return false
    })
    // Sustainability filter
    .filter(
      ({
        taxonomySiteSustainabilities: { nodes: taxonomySiteSustainabilities },
      }) => {
        if (!Object.keys(siteSustainability).length) return true

        for (const key in siteSustainability) {
          if (taxonomySiteSustainabilities.some(({ slug }) => slug === key)) {
            return true
          }
        }

        return false
      }
    )

  callback(filteredData)
}

export const filterSites = (
  data,
  {
    searchTerm,
    priceRange,
    siteDistricts,
    siteTypes,
    siteServices,
    sitePublicTransports,
    siteParking,
    unitAttributes,
    unitTechnicalAttributes,
    unitArea,
    availability,
    focusedSiteId,
  },
  callback
) => {
  let filteredData = data
    //Search term filter
    .filter(({ title, siteMeta }) => {
      if (searchTerm.length >= 3) {
        const lowerCaseTerm = searchTerm.toLowerCase()
        return (
          title?.toLowerCase().includes(lowerCaseTerm) ||
          siteMeta?.fieldEqSiteFieldsAddress
            ?.toLowerCase()
            .includes(lowerCaseTerm) ||
          siteMeta?.fieldEqSiteFieldsCommercialName
            ?.toLowerCase()
            .includes(lowerCaseTerm)
        )
      }

      return true
    })
    // Site district filter (siteAreaTaxonomy children)
    .filter(({ taxonomySiteAreas: { nodes: taxonomySiteAreas } }) => {
      if (!Object.keys(siteDistricts).length) return true

      const districts = findDistricts(taxonomySiteAreas)

      for (const key in siteDistricts) {
        if (districts.some((district) => district.slug === key)) return true
      }

      return false
    })
    //Site type filter
    .filter(({ taxonomySiteTypes: { nodes: taxonomySiteTypes } }) => {
      if (!Object.keys(siteTypes).length) return true

      for (const key in siteTypes)
        if (taxonomySiteTypes[0].slug === key) return true

      return false
    })
    //Site service filter
    .filter(({ taxonomySiteServices: { nodes: taxonomySiteServices } }) => {
      let selectedCount = Object.keys(siteServices).length
      let matchedCount = 0

      for (const key in siteServices) {
        if (taxonomySiteServices.some((el) => el.slug === key)) {
          matchedCount++
        }
      }

      return selectedCount === 0 || selectedCount === matchedCount
    })
    //Public transport filter
    .filter(({ siteMeta }) => {
      let selectedCount = Object.keys(sitePublicTransports).length
      let matchedCount = 0

      for (const key in sitePublicTransports) {
        const splitKey = key.split("-")
        const field = splitKey[0]
        const timeInMinutes = Number(splitKey[1])

        if (siteMeta[field] && Number(siteMeta[field]) <= timeInMinutes) {
          matchedCount++
        }
      }

      return selectedCount === 0 || selectedCount === matchedCount
    })
    // Parking filter
    .filter(({ siteMeta }) => {
      let selectedCount = Object.keys(siteParking).length
      let matchedCount = 0

      for (const key in siteParking) {
        if (siteMeta[key] && Number(siteMeta[key]) > 0) {
          matchedCount++
        }
      }

      return selectedCount === 0 || selectedCount === matchedCount
    })
    //Unit filters
    .reduce((currentValue, site) => {
      const unitData = { ...site }
      unitData.siteUnits = unitData.siteUnits
        // Unit attributes filter
        .filter(
          ({
            taxonomyUnitAreaRanges: { nodes: taxonomyUnitAreaRanges },
            unitMeta: {
              fieldEqUnitFieldsExtensibility,
              fieldEqUnitFieldsDivisibility,
            },
          }) => {
            const showUnitForLowerUnitAreaValues = [
              "Voidaan laajentaa ylös/alaspäin",
              "Voidaan laajentaa viereisiin tiloihin",
              "Tietyin edellytyksin mahdollista",
            ]

            const showUnitForLargerUnitAreaValues = [
              "Voidaan vuokrata myös vain osittain",
              "Tietyin edellytyksin mahdollista",
            ]

            if (unitArea && taxonomyUnitAreaRanges[0]) {
              const unitAreaRange =
                taxonomyUnitAreaRanges[0].slug === "yli-1000"
                  ? [1000]
                  : taxonomyUnitAreaRanges[0].slug
                      .split("-")
                      .map((item) => Number(item))
              const areaFilterRange =
                unitArea.name === "yli-1000"
                  ? [1000]
                  : unitArea.name.split("-").map((item) => Number(item))
              const maxFilterRange =
                areaFilterRange.length >= 1
                  ? areaFilterRange[1]
                  : areaFilterRange[0]
              const isSmaller = unitAreaRange[0] < areaFilterRange[0]
              const isLarger =
                unitAreaRange.length >= 1
                  ? unitAreaRange[1] > maxFilterRange
                  : unitAreaRange[0] > maxFilterRange

              if (
                (isSmaller &&
                  showUnitForLowerUnitAreaValues.includes(
                    fieldEqUnitFieldsExtensibility
                  )) ||
                (isLarger &&
                  showUnitForLargerUnitAreaValues.includes(
                    fieldEqUnitFieldsDivisibility
                  ))
              ) {
                return true
              }

              return taxonomyUnitAreaRanges[0].slug === unitArea.name
            } else if (unitArea && !taxonomyUnitAreaRanges[0]) {
              return false
            }

            return true
          }
        )
        //Price range filter
        .filter(({ unitMeta: { fieldEqUnitFieldsMonthlyPriceEstimate } }) => {
          if (
            (!priceRange?.min && !priceRange?.max) ||
            isNaN(priceRange.min) ||
            isNaN(priceRange.max) ||
            !fieldEqUnitFieldsMonthlyPriceEstimate
          ) {
            return true
          }
          let fitsInRange = false

          if (priceRange.min) {
            fitsInRange =
              Number(priceRange.min) <=
              Number(fieldEqUnitFieldsMonthlyPriceEstimate)
          }

          if (priceRange.max) {
            fitsInRange =
              Number(priceRange.max) >=
              Number(fieldEqUnitFieldsMonthlyPriceEstimate)
          }

          return fitsInRange
        })
        .filter(
          ({
            taxonomyUnitAvailabilities: { nodes: taxonomyUnitAvailabilities },
          }) => {
            if (!taxonomyUnitAvailabilities[0]?.slug) return true
            else if (availability?.name === AVAILABLE_SLUG) {
              return availability.name === taxonomyUnitAvailabilities[0].slug
            } else if (availability === AVAILABLE_SOON_SLUG) {
              return true
            }

            return true
          }
        )
        .filter(
          ({ taxonomyUnitAttributes: { nodes: taxonomyUnitAttributes } }) => {
            let selectedCount = Object.keys(unitAttributes).length
            let matchedCount = 0

            for (const key in unitAttributes) {
              if (taxonomyUnitAttributes?.some((el) => el.slug === key)) {
                matchedCount++
              }
            }
            return selectedCount === 0 || selectedCount === matchedCount
          }
        )
        // Unit technical attributes filter
        .filter(
          ({
            taxonomyUnitTechnicalAttributes: {
              nodes: taxonomyUnitTechnicalAttributes,
            },
          }) => {
            let selectedCount = Object.keys(unitTechnicalAttributes).length
            let matchedCount = 0

            for (const key in unitTechnicalAttributes) {
              if (
                taxonomyUnitTechnicalAttributes?.some((el) => el.slug === key)
              ) {
                matchedCount++
              }
            }
            return selectedCount === 0 || selectedCount === matchedCount
          }
        )
        .sort(
          (
            { unitMeta: { fieldEqUnitFieldsArea: firstFieldEqUnitFieldsArea } },
            { unitMeta: { fieldEqUnitFieldsArea: secondFieldEqUnitFieldsArea } }
          ) => {
            return secondFieldEqUnitFieldsArea - firstFieldEqUnitFieldsArea
          }
        )
        .sort(
          (
            {
              unitMeta: {
                fieldEqUnitFieldsCampaignActive: firstCampaignActive,
              },
            },
            {
              unitMeta: {
                fieldEqUnitFieldsCampaignActive: secondCampaignActive,
              },
            }
          ) => {
            if (firstCampaignActive && !secondCampaignActive) {
              return -1
            } else if (!firstCampaignActive && secondCampaignActive) {
              return 1
            } else {
              return 0
            }
          }
        )
      const vacantUnits =
        site?.siteMeta?.fieldEqSiteFieldsNumOfAvailableUnits ?? 0
      const unitsArea =
        site?.siteMeta?.fieldEqSiteFieldsNumOfAvailableSpace ?? 0

      const siteExtendable =
        LARGE_AREA_FILTERS.includes(unitArea?.name) &&
        unitsArea > UNITS_EXTENDABLE_AREA &&
        parseFloat(site?.siteMeta.fieldEqSiteFieldsGrossArea) >
          LARGE_SITE_AREA &&
        vacantUnits > 1

      if (unitData.siteUnits.length || siteExtendable) {
        currentValue.push(unitData)
      }
      return currentValue
    }, [])
    .sort(
      (
        {
          siteMeta: { fieldEqSiteFieldsCommercialName: firstName },
          title: firstTitle,
        },
        {
          siteMeta: { fieldEqSiteFieldsCommercialName: secondName },
          title: secondTitle,
        }
      ) => {
        const firstUnitTitle = firstName || firstTitle
        const secondUnitTitle = secondName || secondTitle

        if (firstUnitTitle < secondUnitTitle) {
          return -1
        } else if (firstUnitTitle > secondUnitTitle) {
          return 1
        }

        return 0
      }
    )
    .sort(
      (
        { siteMeta: { fieldEqSiteFieldsCampaignActive: firstCampaignActive } },
        { siteMeta: { fieldEqSiteFieldsCampaignActive: secondCampaignActive } }
      ) => {
        if (firstCampaignActive && !secondCampaignActive) {
          return -1
        } else if (!firstCampaignActive && secondCampaignActive) {
          return 1
        } else {
          return 0
        }
      }
    )

  if (focusedSiteId) {
    filteredData = filteredData.sort(
      ({ id: firstSiteId }, { id: secondSiteId }) => {
        if (firstSiteId === focusedSiteId) {
          return -1
        } else if (secondSiteId === focusedSiteId) {
          return 1
        }

        return 0
      }
    )
  }

  callback(filteredData)
}
